import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { collection, doc, setDoc, serverTimestamp, getDocs } from "firebase/firestore";
import * as utils from './Utils.js';
import { db } from '../firebase.js';

const slugify = (str) =>
  str
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");

const AdminEvents = () => {
  const [currentUser, setCurrentUser] = useState(null);

  // Form state
  const [name, setName] = useState("");
  const [eventId, setEventId] = useState("");
  const [startDate, setStartDate] = useState(""); // date string
  const [startTime, setStartTime] = useState(""); // time string
  const [endDate, setEndDate] = useState(""); // date string
  const [endTime, setEndTime] = useState(""); // time string
  const [location, setLocation] = useState({
    name: "",
    line1: "",
    line2: "",
    city: "",
    state: "",
    zip: "",
    country: "USA"
  });
  const [rsvpType, setRsvpType] = useState("yes_no");
  const [mealOptions, setMealOptions] = useState([]);

  // Loaded events
  const [events, setEvents] = useState([]);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setCurrentUser(user);
        fetchEvents();
      } else {
        window.location.href = "/login";
      }
    });
    return () => unsubscribe();
  }, []);

  const handleNameChange = (value) => {
    setName(value);
    setEventId(slugify(value));
  };

  const addMealOption = () => {
    setMealOptions([...mealOptions, { id: "", name: "", description: "", tag: "" }]);
  };

  const updateMealOption = (index, field, value) => {
    const updated = [...mealOptions];

    if (field === "name") {
      updated[index].name = value;
      updated[index].id = slugify(value);
    } else {
      updated[index][field] = value;
    }

    setMealOptions(updated);
  };

  // Converts input date + time (from HTML inputs) to a proper UTC Date assuming ET
  const parseEasternInputToUTC = (dateStr, timeStr) => {
    if (!dateStr || !timeStr) return null;

    const [year, month, day] = dateStr.split("-").map(Number);
    const [hour, minute] = timeStr.split(":").map(Number);

    // Create a Date object using local time for formatting
    const wallTime = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));

    // Use Intl API to get the offset from UTC for ET at this date
    const etOffsetMinutes = (() => {
      const dtf = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/New_York",
        hour12: false,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit"
      });
      const parts = dtf.formatToParts(wallTime);
      const etHour = Number(parts.find(p => p.type === "hour").value);
      const etMinute = Number(parts.find(p => p.type === "minute").value);
      const etSecond = Number(parts.find(p => p.type === "second").value);
      const etYear = Number(parts.find(p => p.type === "year").value);
      const etMonth = Number(parts.find(p => p.type === "month").value) - 1;
      const etDay = Number(parts.find(p => p.type === "day").value);

      const etDate = new Date(Date.UTC(etYear, etMonth, etDay, etHour, etMinute, etSecond));

      // Difference in minutes between UTC and ET
      return (etDate.getTime() - wallTime.getTime()) / 60000;
    })();

    // Subtract ET offset to get UTC timestamp
    const utcTime = new Date(wallTime.getTime() - etOffsetMinutes * 60000);

    return utcTime;
  };


  const saveEvent = async () => {
    if (!eventId || !name) {
      alert("Event name required");
      return;
    }

    const start = parseEasternInputToUTC(startDate, startTime);
    const end = parseEasternInputToUTC(endDate, endTime);

    const eventDoc = {
      name,
      start,
      end,
      location,
      rsvpType,
      mealOptions: rsvpType === "meal" ? mealOptions : [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(doc(db, "events", eventId), eventDoc);
    alert("Event saved");
    fetchEvents();
  };

  const fetchEvents = async () => {
    const snapshot = await getDocs(collection(db, "events"));
    const loaded = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    // Sort by start time ascending
    loaded.sort((a, b) => {
      const aStart = a.start?.toDate ? a.start.toDate() : new Date(a.start);
      const bStart = b.start?.toDate ? b.start.toDate() : new Date(b.start);
      return aStart - bStart;
    });

    setEvents(loaded);
  };

  const editEvent = (event) => {
    setName(event.name);
    setEventId(event.id);
    // Convert Firestore timestamps to ET strings for inputs
    const st = event.start?.toDate ? event.start.toDate() : event.start;
    const et = event.end?.toDate ? event.end.toDate() : event.end;

    if (st) {
      const stET = st.toLocaleString("en-US", { timeZone: "America/New_York" });
      const stDate = stET.split(",")[0]; // "1/1/2026"
      const stTime = stET.split(",")[1].trim(); // "18:30:00"
      setStartDate(new Date(st).toISOString().split("T")[0]); // "YYYY-MM-DD"
      setStartTime(st.toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit", timeZone: "America/New_York" }));
    } else {
      setStartDate("");
      setStartTime("");
    }

    if (et) {
      setEndDate(new Date(et).toISOString().split("T")[0]);
      setEndTime(et.toLocaleTimeString("en-US", { hour12: false, hour: "2-digit", minute: "2-digit", timeZone: "America/New_York" }));
    } else {
      setEndDate("");
      setEndTime("");
    }

    setLocation(event.location || {});
    setRsvpType(event.rsvpType || "yes_no");
    setMealOptions(event.mealOptions || []);
  };

  if (!currentUser) return null;

  return (
    <div className="AdminEvents">
      <div className='centered-box wedding-page-block'>
        <div className='wedding-centerpiece'>
          <p style={{margin: '15px 0 0 0', width: '100%'}}><a style={{textDecoration: 'none', color: '#00233f', padding: '5px', border: '1px solid transparent'}} href="/admin"><b>← Back to Admin</b></a></p>
          <p className='wedding-page-title'>Events</p>
        </div>

        {/* Events Table */}
        <div className="card p-4 shadow-sm mb-4">
          <table className="table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Start</th>
                <th>End</th>
                <th>RSVP Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.map(evt => (
                <tr key={evt.id}>
                  <td>{evt.name}</td>
                  <td>{evt.start?.toDate ? evt.start.toDate().toLocaleString("en-US", { timeZone: "America/New_York" }) : ""}</td>
                  <td>{evt.end?.toDate ? evt.end.toDate().toLocaleString("en-US", { timeZone: "America/New_York" }) : ""}</td>
                  <td>{evt.rsvpType == 'meal' ? 'Meal' : 'Yes / No'}</td>
                  <td>
                    <a className="btn btn-outline-navyblue" onClick={() => editEvent(evt)}>Edit</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Event Form */}
        <div className="card p-4 shadow-sm">
          <div className="mb-3">
            <label className="form-label">Event Name</label>
            <input type="text" className="form-control" value={name} onChange={(e) => handleNameChange(e.target.value)} />
            {eventId && <small className="text-muted">Event ID: <code>{eventId}</code></small>}
          </div>

          {/* Start & End */}
          <div className="row mb-4">
            <div className="col-md-3">
              <label className="form-label">Start Date</label>
              <input type="date" className="form-control" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label">Start Time (ET)</label>
              <input type="time" className="form-control" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label">End Date</label>
              <input type="date" className="form-control" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </div>
            <div className="col-md-3">
              <label className="form-label">End Time (ET)</label>
              <input type="time" className="form-control" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
            </div>
          </div>

          {/* Location */}
          <h5 className="mb-3">Location</h5>
          <div className="mb-3">
            <label className="form-label">Venue Name</label>
            <input type="text" className="form-control" value={location.name} onChange={(e) => setLocation({ ...location, name: e.target.value })} />
          </div>
          <div className="mb-3">
            <label className="form-label">Address Line 1</label>
            <input type="text" className="form-control" value={location.line1} onChange={(e) => setLocation({ ...location, line1: e.target.value })} />
          </div>
          <div className="mb-3">
            <label className="form-label">Address Line 2</label>
            <input type="text" className="form-control" value={location.line2} onChange={(e) => setLocation({ ...location, line2: e.target.value })} />
          </div>
          <div className="row">
            <div className="col-md-5 mb-3">
              <label className="form-label">City</label>
              <input type="text" className="form-control" value={location.city} onChange={(e) => setLocation({ ...location, city: e.target.value })} />
            </div>
            <div className="col-md-3 mb-3">
              <label className="form-label">State</label>
              <input type="text" className="form-control" value={location.state} onChange={(e) => setLocation({ ...location, state: e.target.value })} />
            </div>
            <div className="col-md-2 mb-3">
              <label className="form-label">ZIP</label>
              <input type="text" className="form-control" value={location.zip} onChange={(e) => setLocation({ ...location, zip: e.target.value })} />
            </div>
            <div className="col-md-2 mb-3">
              <label className="form-label">Country</label>
              <input type="text" className="form-control" value={location.country} onChange={(e) => setLocation({ ...location, country: e.target.value })} />
            </div>
          </div>

          {/* RSVP Type */}
          <div className="mb-4">
            <label className="form-label">RSVP Type</label>
            <select className="form-select" value={rsvpType} onChange={(e) => setRsvpType(e.target.value)}>
              <option value="yes_no">Yes / No</option>
              <option value="meal">Meal Selection</option>
            </select>
          </div>

          {/* Meal Options */}
          {rsvpType === "meal" && mealOptions.map((option, idx) => (
            <div key={idx} className="border rounded p-3 mb-3">
              <div className="mb-2">
                <label className="form-label">Meal Name</label>
                <input className="form-control" value={option.name} onChange={(e) => updateMealOption(idx, "name", e.target.value)} />
              </div>
              <div className="mb-2">
                <label className="form-label">Meal ID</label>
                <input className="form-control" value={option.id} disabled />
              </div>
              <div>
                <label className="form-label">Description</label>
                <input className="form-control" value={option.description} onChange={(e) => updateMealOption(idx, "description", e.target.value)} />
              </div>
              <div>
                <label className="form-label">Tag</label>
                <input className="form-control" value={option.tag} onChange={(e) => updateMealOption(idx, "tag", e.target.value)} />
              </div>
            </div>
          ))}
          {rsvpType === "meal" && <a className="btn btn-outline-navyblue mb-4" onClick={addMealOption}>Add Meal Option</a>}

          {/* Save Button */}
          <div className="text-end">
            <a className="btn btn-navyblue" onClick={saveEvent}>Save Event</a>
          </div>
        </div>
      </div>

      <div className='centered-box'>
        { utils.displayCheckerboard(20, 42, ['black', 'ivory']) }
      </div>
    </div>
  );
};

export default AdminEvents;
